/**
 * The phone Control Panel's state across a trip to a pour dashboard and
 * back: filter, search, loaded pages, the list and the scroll position, in
 * memory only (site phones may be shared, so no pour data goes to
 * localStorage). It belongs to one user: loadSnapshot returns it only for
 * the same userKey. A stop made on the dashboard patches the row and marks
 * the snapshot stale, so the list shows the change at once and reloads.
 *
 * The filter and search alone are also kept in sessionStorage, so a
 * reloaded tab keeps them. Logging out clears both (mobileSessionCaches).
 */
import type { Pour } from '../../services/pours';
import { getUserCompanyId } from '../../utils/userPermissions';
import { registerMobileCacheClear } from '../../utils/mobileSessionCaches';

export interface ControlPanelSnapshot {
  /** The owner, from controlPanelUserKey. */
  userKey: string;
  statusFilter: string;
  search: string;
  /** Server pages loaded (Load more). */
  pagesLoaded: number;
  pours: Pour[];
  /** The server's total for the filter. */
  total: number;
  /** Epoch ms of the last successful list fetch. */
  fetchedAt: number;
  scrollY: number;
  /** Something changed since the fetch (a stop): reload on return. */
  stale: boolean;
}

export interface ControlPanelFilters {
  statusFilter: string;
  search: string;
}

/** sessionStorage key for { userKey, statusFilter, search }. */
export const FILTERS_STORAGE_KEY = 'conxedge.cp.mobileFilters';

let snapshot: ControlPanelSnapshot | null = null;

const emptySnapshot = (userKey: string): ControlPanelSnapshot => ({
  userKey,
  // The Control Panel's default filter.
  statusFilter: 'In progress',
  search: '',
  pagesLoaded: 0,
  pours: [],
  total: 0,
  fetchedAt: 0,
  scrollY: 0,
  stale: false,
});

/**
 * The snapshot's owner key, `${UserID}|${CompanyID}`, compared as text:
 * company IDs are GUIDs and are never parsed. '' without a user, and ''
 * never matches a snapshot.
 */
export const controlPanelUserKey = (
  user: { UserID?: string | null; UserName?: string | null; [key: string]: any } | null | undefined,
): string => {
  if (!user) return '';
  const who = String(user.UserID || user.UserName || '');
  if (!who) return '';
  return `${who}|${getUserCompanyId(user)}`;
};

/** The snapshot when it belongs to userKey, else null. Treat it as
 *  read-only: every change goes through saveSnapshot or patchPour. */
export const loadSnapshot = (userKey: string): ControlPanelSnapshot | null => {
  if (!userKey || !snapshot || snapshot.userKey !== userKey) return null;
  return snapshot;
};

const writeFilters = (value: { userKey: string } & ControlPanelFilters): void => {
  try {
    window.sessionStorage.setItem(
      FILTERS_STORAGE_KEY,
      JSON.stringify({ userKey: value.userKey, statusFilter: value.statusFilter, search: value.search }),
    );
  } catch {
    // Storage blocked or full: the filter is simply not kept on reload.
  }
};

/**
 * Merge `partial` into the user's snapshot (a different user's snapshot is
 * replaced). `stale` is kept unless the partial sets it, so save
 * { stale: false, fetchedAt } with a freshly fetched list.
 */
export const saveSnapshot = (partial: Partial<ControlPanelSnapshot> & { userKey: string }): void => {
  if (!partial.userKey) return;
  const base = snapshot && snapshot.userKey === partial.userKey ? snapshot : emptySnapshot(partial.userKey);
  const next: ControlPanelSnapshot = { ...base };
  // An undefined field leaves the saved value as it is.
  (Object.keys(partial) as Array<keyof ControlPanelSnapshot>).forEach((key) => {
    if (partial[key] !== undefined) (next as any)[key] = partial[key];
  });
  snapshot = next;
  writeFilters(snapshot);
};

/** Update one cached row (when it is loaded) and mark the snapshot stale,
 *  so the Control Panel reloads when it is shown again. */
export const patchPour = (pourId: string, patch: Partial<Pour>): void => {
  if (!snapshot) return;
  snapshot = {
    ...snapshot,
    pours: snapshot.pours.map((p) => (p.PourID === pourId ? { ...p, ...patch } : p)),
    stale: true,
  };
};

export const markSnapshotStale = (): void => {
  if (snapshot) snapshot = { ...snapshot, stale: true };
};

/** Forget the snapshot and the saved filters (logout). */
export const clearSnapshot = (): void => {
  snapshot = null;
  try {
    window.sessionStorage.removeItem(FILTERS_STORAGE_KEY);
  } catch {
    // Storage blocked: nothing was saved.
  }
};

/** The filter and search saved in this tab for userKey, else null. */
export const readSavedFilters = (userKey: string): ControlPanelFilters | null => {
  if (!userKey) return null;
  try {
    const raw = window.sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved || typeof saved !== 'object' || saved.userKey !== userKey) return null;
    return {
      statusFilter: typeof saved.statusFilter === 'string' ? saved.statusFilter : emptySnapshot(userKey).statusFilter,
      search: typeof saved.search === 'string' ? saved.search : '',
    };
  } catch {
    return null;
  }
};

registerMobileCacheClear(clearSnapshot);
